/* eslint-disable react/prop-types */
import React, { memo, useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles'
import InputLabel from '@material-ui/core/InputLabel'
import cloneDeep from 'clone-deep'
import FormControl from '@material-ui/core/FormControl'
import ListItemText from '@material-ui/core/ListItemText'
import MuiSelect from '@material-ui/core/Select'
import Checkbox from '@material-ui/core/Checkbox'
import PropTypes from 'prop-types'

import { MenuItem } from './'
import { useConstructor, useBaseComponentOperations } from '../../hooks'
import { standardBodyFont } from '../../themes'
import { translate } from '../../multilanguage'
import { eventObject, defaultVariant, defaultColor } from './common'

const minWidth = 120
const defaultPadding = '13px'
const defaultPaddingStandard = '6px'
const labelMarginTop = '-7px'
let selectId = 0

const StyledSelect = withStyles(theme => ({
  root: {
    ...standardBodyFont(theme),
    ...props => props.style,
    paddingTop: defaultPaddingStandard,
    paddingBottom: '7px',
    minHeight: '1.1876em',
    lineHeight: '16px'
  },
  outlined: {
    paddingTop: defaultPadding,
    paddingBottom: defaultPadding,
  }
}))(MuiSelect)

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: minWidth
  }
}))

const useStylesListItem = makeStyles(theme => ({
  primary: {
    ...standardBodyFont(theme),
  }
}))

const useStylesMenuItem = makeStyles(theme => ({
  root: {
    padding: '0px',
  }
}))

/**
 * Implements the Mui Select.
 *
 * @param {object} props component props
 * @returns {Select} Select component
 */
export const Select = memo(forwardRef((props, ref) => {
  const eventKey = 'value'
  const theme = useTheme()
  const items = useRef([])
  const selectRef = useRef()
  const [forceRender, setForceRender] = useState(false)
  const idtemplate = `BaseSelect-label${selectId}`
  const classes = useStyles()
  const [value, onChange, setValue] = useBaseComponentOperations(props, eventKey, props.defaultValue || '')
  const { noTranslate, noTranslateItems, value: valueProp, ...finalProps } = props

  const handleItemsProp = () => {
    items.current = cloneDeep(props.items)
    if (!props.noTranslateItems) {
      items.current.forEach(e => e.label = translate(e.label))
    }
  }

  useConstructor(() => {
    ++selectId
    handleItemsProp()
  })

  useEffect(() => {
    handleItemsProp()
    setForceRender(!forceRender)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    focus: () => selectRef.current.focus(),
    getValue: () => value,
    setValue: value => {
      setValue(value)
      onChange(eventObject(eventKey, value))
    }
  }))

  let selectItems = []
  if (Array.isArray(items.current)) {
    selectItems = items.current.map((item, i) => <MenuItem noTranslate key={i} value={item.value}>{item.label}</MenuItem>)
  }

  return (
    <FormControl variant={props.variant} fullWidth className={classes.formControl}>
      {props.label && <InputLabel style={{ ...standardBodyFont(theme), marginTop: labelMarginTop }} id={idtemplate}>{props.noTranslate ? props.label : translate(props.label)}</InputLabel>}
      <StyledSelect
        inputRef={selectRef}
        style={{ ...props.style }}
        {...finalProps}
        labelId={idtemplate}
        label={props.noTranslate ? props.label : translate(props.label)}
        value={value !== undefined ? value : ''}
        onChange={onChange}
      >
        {selectItems}
      </StyledSelect>
    </FormControl>
  )
}))

Select.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Initial value for the select.
   */
  defaultValue: PropTypes.string,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when the Select is clicked.
   */
  onChange: PropTypes.func,
  /**
   Items to display in the radio group.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the items labels.
   */
  noTranslateItems: PropTypes.bool,
  /**
   Style for the control.
   */
  style: PropTypes.object,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['outlined', 'standard']),
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Puts the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Display empty value label - default false
   */
  displayEmpty: PropTypes.bool
}

Select.defaultProps = {
  noTranslate: false,
  noTranslateItems: false,
  variant: defaultVariant,
  displayEmpty: false
}

/**
 * Implements a multi MUI Select with the items as checkboxes.
 *
 * @param {object} props component props
 * @returns {MultiSelectWithCheckboxes} MultiSelectWithCheckboxes component
 */
export const MultiSelectWithCheckboxes = memo(forwardRef((props, ref) => {
  const theme = useTheme()
  const items = useRef([])
  const { id, noTranslate, noTranslateItems, appliedFilters, checkboxColor, centerList, districtsList, ...finalProps } = props
  const [forceRender, setForceRender] = useState(false)
  const [prevSelectedRecords, setPrevSelectedRecords] = useState(appliedFilters?.districtId)
  const idtemplate = `BaseSelect-label${selectId}`
  const classes = useStyles()
  const [value, setValue] = useState(props.defaultValue || [])
  const listItemClasses = useStylesListItem()
  const menuItemClasses = useStylesMenuItem()

  const handleItemsProp = () => {
    items.current = cloneDeep(props.items)
    if (!props.noTranslateItems) {
      items.current.forEach(e => e.label = translate(e.label))
    }
  }

  useConstructor(() => {
    ++selectId
    handleItemsProp()
  })

  useEffect(() => {
    handleItemsProp()
    setForceRender(!forceRender)
  }, [props.items])

  const onChange = (event) => {
    const data = event.target.value.filter((item, index) => event.target.value.indexOf(item) === index)

    if (id === 'filter__district') {
      const arrayToAutoCheckDistrictBuildings = []
      // code to select all child records if user check on all option
      data.forEach((docCenter) => {
        if (centerList.includes(docCenter) && !prevSelectedRecords.includes(docCenter)) {
          const docCenterSplitted = docCenter.split('-')
          districtsList?.forEach((docDistrict) => {
            const docDistrictSplitted = docDistrict.value.split('-')
            if (docCenterSplitted[0] === docDistrictSplitted[0]) {
              arrayToAutoCheckDistrictBuildings.push(docDistrict.value)
            }
          })
        } else {
          arrayToAutoCheckDistrictBuildings.push(docCenter)
        }
      })

      const dataUnique = arrayToAutoCheckDistrictBuildings.filter((item, index) => 
        arrayToAutoCheckDistrictBuildings.indexOf(item) === index)

      const arrayDiffOldToNew = prevSelectedRecords.filter(e => !dataUnique.find(a => e === a))

      // code to un select all child records if user uncheck on all option
      arrayDiffOldToNew.forEach((removedDistricts) => {
        const removedDistrictsSplitted = removedDistricts.split('-')
        if (centerList.includes(removedDistricts) && removedDistrictsSplitted[1] === 'ALL') {
          districtsList?.forEach((docDistrict) => {
            const docDistrictSplitted = docDistrict.value.split('-')
            if (removedDistrictsSplitted[0] === docDistrictSplitted[0] && dataUnique.includes(docDistrict.value)) {
              const indexOfDistrict = dataUnique.indexOf(docDistrict.value)
              dataUnique.splice(indexOfDistrict, 1)
            }
          })
        }
      })

      // code to un check all option if user uncheck any single child from the group
      prevSelectedRecords.forEach((prevSelectedRecord) => {
        if (!dataUnique.includes(prevSelectedRecord)) {
          const removedCenterRecord = prevSelectedRecord.split('-')
          if (dataUnique.includes(`${removedCenterRecord[0]}-ALL`)) {
            const indexOfAllToBeRemoved = dataUnique.includes(`${removedCenterRecord[0]}-ALL`)
            dataUnique.splice(indexOfAllToBeRemoved, 1)
          }
        }
      })
      
      setPrevSelectedRecords(dataUnique)
      setValue(dataUnique)
      props.onChange && props.onChange(dataUnique)
    } else {
      setValue(data)
      props.onChange && props.onChange(data)
    }
  }

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    setValue: value => {
      if (value === undefined) {
        value = []
      }
      if (!Array.isArray(value)) {
        throw new Error('MultiSelectWithCheckboxes setValue requires an array.')
      }
      setValue(value)
      props.onChange && props.onChange(value)
    }
  }))

  const onClose = () => {
    props.onClose && props.onClose(value)
  }

  const selectItems = items.current.map(item => (
    <MenuItem noTranslate key={item.value} value={item.value} classes={menuItemClasses} >
      <>
        <Checkbox checked={(value.findIndex(selectedItem => selectedItem === item.value) > -1)} color={props.checkboxColor || 'primary'} />
        <ListItemText primary={item.label.split('-')[1] === 'ALL' ? <b>{item.label}</b> : item.label} classes={listItemClasses} />
      </>
    </MenuItem>
  ))

  const getSelectedLabels = () => {
    const labels = []
    items.current.forEach(item => {
      if ((value.findIndex(selectedItem => selectedItem === item.value) > -1)) {
        labels.push(item.label)
      }
    })
    return labels
  }

  return (
    <FormControl variant={props.variant} fullWidth className={classes.formControl}>
      {props.label && <InputLabel style={{ ...standardBodyFont(theme), marginTop: labelMarginTop }} id={idtemplate}>{props.noTranslate ? props.label : translate(props.label)}</InputLabel>}
      <StyledSelect
        {...finalProps}
        multiple
        style={{ ...props.style }}
        labelId={idtemplate}
        label={props.noTranslate ? props.label : translate(props.label)}
        value={value}
        onChange={onChange}
        onClose={onClose}
        renderValue={() => getSelectedLabels().join(', ')}
      >
        {selectItems}
      </StyledSelect>
    </FormControl>
  )
}))

MultiSelectWithCheckboxes.propTypes = {
  /**
   Initial value for the select.
   */
  defaultValue: PropTypes.array,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when the Select is clicked.
   */
  onChange: PropTypes.func,
  /**
   Items to display in the radio group.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Prevents the language translation of the items labels.
   */
  noTranslateItems: PropTypes.bool,
  /**
   Style for the control.
   */
  style: PropTypes.object,
  /**
   MUI variant for the control.
   */
  variant: PropTypes.oneOf(['outlined', 'standard']),
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Puts the control in an error state.
   */
  error: PropTypes.bool,
  /**
   MUI color for the checkboxes in the select.
   */
  checkboxColor: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   onClose function called when close of select window
   */
  onClose: PropTypes.func
}

MultiSelectWithCheckboxes.defaultProps = {
  noTranslate: false,
  noTranslateItems: false,
  variant: defaultVariant,
  checkboxColor: defaultColor
}